<template>
  <section id="planes-2027" class="plans-tabs-section">
    <div class="container">
      <div class="section-header text-center mb-4">
        <h2 class="section-title">Cinco planes, un precio para cada bolsillo</h2>
        <p class="section-subtitle">Elige un plan para ver qué incluye.</p>
      </div>

      <div class="plans-bar">
        <div class="tabs" role="tablist" aria-label="Planes">
          <button
            v-for="plan in plans"
            :key="plan.id"
            type="button"
            role="tab"
            class="tab-btn"
            :aria-selected="current === plan.id"
            :tabindex="current === plan.id ? 0 : -1"
            @click="current = plan.id"
          >
            {{ plan.name }}
          </button>
        </div>
      </div>

      <div v-if="activePlan" class="plan-panel" role="tabpanel">
        <div class="plan-main">
          <span v-if="activePlan.badge" class="badge">{{ activePlan.badge }}</span>
          <h3>Plan {{ activePlan.name }}</h3>
          <div class="plan-price">
            {{ priceLine.big }}<small> {{ priceLine.small }}</small>
          </div>
          <div v-if="showIntervals" class="interval-picker" role="group" aria-label="Forma de pago">
            <button
              v-for="price in availablePrices"
              :key="price.billing_interval"
              type="button"
              :aria-pressed="billingInterval === price.billing_interval"
              @click="billingInterval = price.billing_interval"
            >
              {{ intervalName(price.billing_interval) }}
            </button>
          </div>
          <p v-if="offersPrepaidWellness" class="interval-note">
            Pagando trimestral o anual, tus exámenes del Wellness están disponibles desde el día 1.
          </p>

          <template v-if="activePlan.fam">
            <div class="plan-was">
              {{ famN }} personas · ${{ perPerson }}/persona
            </div>
            <div class="fam-sizes" role="group" aria-label="Tamaño de familia">
              <button
                v-for="n in familySizes"
                :key="n"
                type="button"
                :aria-pressed="n === famN"
                @click="famN = n"
              >
                {{ n }} personas
              </button>
            </div>
            <div v-if="famMarketLine" class="plan-mkt">{{ famMarketLine }}</div>
          </template>
          <template v-else>
            <div class="plan-was">
              {{ activePlan.unit || 'al mes' }}
              <span v-if="activePlan.was"> · antes ${{ activePlan.was }}</span>
            </div>
            <div v-if="activePlan.mkt" class="plan-mkt">{{ activePlan.mkt }}</div>
          </template>

          <div v-if="priceLine.sub" class="plan-was">{{ priceLine.sub }}</div>
          <p class="plan-for">{{ activePlan.for }}</p>

          <button
            v-if="purchaseDisabled"
            type="button"
            class="btn btn-plan-cta btn-plan-cta--soon"
            disabled
          >
            Próximamente
          </button>
          <button
            v-else
            type="button"
            class="btn btn-plan-cta"
            @click="goToPurchase"
          >
            Quiero este plan <i class="fas fa-chevron-right ml-2"></i>
          </button>
        </div>

        <div class="benefits">
          <ul>
            <li v-for="(b, i) in activePlan.benefits" :key="i">
              <b>{{ b[0] }}</b>
              <span>{{ b[1] }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import ToastificationContent from '@/core/plugins/toastification/ToastificationContent.vue'
import {
  HMP_PLANS_2027,
  famPrice,
  matchApiPlan,
  isOnlinePurchaseDisabled
} from '../data/hmpPlans2027'
import { familyTierAmount, intervalCopy, pickBillingPrice, planBillingPrices } from '@/core/utils/membershipBilling'
import { FAMILY_MAX_PEOPLE } from '@/core/utils/membershipFamily'

/**
 * Sección de planes estilo mockup 2027: 5 tabs + tramos Familiar del API.
 * CTA navega a /comprar-plan/:id (Stripe Checkout).
 */
export default {
  name: 'PlansTabsSection',
  props: {
    apiPlans: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      plans: HMP_PLANS_2027,
      current: 'tusalud',
      famN: 4,
      familySizes: Array.from({ length: FAMILY_MAX_PEOPLE - 1 }, (_, index) => index + 2),
      billingInterval: 'month'
    }
  },
  computed: {
    activePlan() {
      return this.plans.find((p) => p.id === this.current) || this.plans[0]
    },
    monthlyPrice() {
      if (!this.activePlan) return 0
      if (this.activePlan.fam) {
        const offer = pickBillingPrice(planBillingPrices(this.matchedApiPlan), 'month')
        if (offer) return familyTierAmount(offer, this.famN)
        return famPrice(this.famN)
      }
      const api = this.matchedApiPlan
      if (api && api.price != null && api.price !== '') {
        return Number(api.price) || 0
      }
      return Number(this.activePlan.price) || 0
    },
    perPerson() {
      if (!this.activePlan || !this.activePlan.fam || !this.famN) return 0
      const amount = this.selectedOffer ? Number(this.selectedOffer.amount) : this.monthlyPrice
      return (amount / this.famN).toFixed(2).replace(/\.00$/, '')
    },
    availablePrices() {
      if (!this.activePlan) return []
      const prices = planBillingPrices(this.matchedApiPlan, this.activePlan.fam ? null : this.monthlyPrice)
      if (!this.activePlan.fam) return prices
      return prices.map((price) => ({
        ...price,
        amount: familyTierAmount(price, this.famN)
      }))
    },
    showIntervals() {
      return this.availablePrices.length > 1
    },
    selectedOffer() {
      return pickBillingPrice(this.availablePrices, this.billingInterval)
    },
    offersPrepaidWellness() {
      return this.availablePrices.some((price) => price.billing_interval === 'quarter' || price.billing_interval === 'year')
    },
    priceLine() {
      if (this.selectedOffer) {
        const copy = intervalCopy(this.selectedOffer.billing_interval)
        return { big: `$${this.selectedOffer.amount}`, small: copy.suffix, sub: null }
      }
      const m = this.monthlyPrice
      return { big: `$${m}`, small: '/mes', sub: null }
    },
    famMarketLine() {
      if (!this.activePlan || !this.activePlan.fam) return ''
      const mk = (this.activePlan.marketBySize || {})[this.famN]
      if (!mk || mk <= this.monthlyPrice) return ''
      if (this.famN === 5) return 'Otro plan del mercado: $189 – $199.'
      return `Otro plan del mercado: $${mk}.`
    },
    matchedApiPlan() {
      return matchApiPlan(this.activePlan, this.apiPlans)
    },
    purchaseDisabled() {
      return isOnlinePurchaseDisabled(this.activePlan && this.activePlan.id)
    }
  },
  watch: {
    current() {
      this.keepBillingInterval()
    },
    apiPlans() {
      this.keepBillingInterval()
    }
  },
  methods: {
    intervalName(interval) {
      return intervalCopy(interval).name
    },
    keepBillingInterval() {
      const prices = this.availablePrices
      if (!prices.some((price) => price.billing_interval === this.billingInterval)) {
        this.billingInterval = prices.length ? prices[0].billing_interval : 'month'
      }
    },
    /**
     * Desde la calculadora familiar abre la compra del plan que corresponde.
     * @param {number} totalPeople
     */
    openFromCalculator(totalPeople) {
      const n = Number(totalPeople) || 1
      if (n <= 1) {
        this.current = 'tusalud'
        this.$nextTick(() => {
          this.goToPurchase()
        })
        return
      }
      this.current = 'familiar'
      this.famN = Math.min(FAMILY_MAX_PEOPLE, Math.max(2, n))
      this.$nextTick(() => {
        this.goToPurchase({ people: this.famN })
      })
    },
    goToPurchase(extraQuery) {
      // Desde el botón llega el MouseEvent: en Familiar se usa el tamaño elegido en la pestaña.
      const people = extraQuery && extraQuery.people != null
        ? extraQuery.people
        : (this.activePlan && this.activePlan.fam ? this.famN : null)
      const extra = people != null ? { people } : {}
      if (this.purchaseDisabled) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Próximamente',
            icon: 'AlertCircleIcon',
            variant: 'info',
            text: 'Este plan aún no está disponible para pago en línea.'
          }
        })
        return
      }
      if (!this.matchedApiPlan || !this.matchedApiPlan.id) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Plan no disponible',
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: 'Este plan aún no está disponible para compra en línea. Contáctanos para ayudarte.'
          }
        })
        return
      }
      this.$router.push({
        name: 'comprar-plan',
        params: { planId: String(this.matchedApiPlan.id) },
        query: {
          billing_interval: (this.selectedOffer && this.selectedOffer.billing_interval) || 'month',
          ...extra
        }
      }).catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.plans-tabs-section {
  padding: 80px 0;
  background: $landing-bg-gray;
}

.section-title {
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: 800;
  color: $landing-primary-color;
  margin-bottom: 0.5rem;
}

.section-subtitle {
  color: $landing-text-medium;
  font-size: 1.1rem;
  margin: 0;
}

.plans-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tab-btn {
  border: 1px solid $landing-border-light;
  background: #fff;
  color: $landing-text-dark;
  border-radius: 999px;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &[aria-selected='true'] {
    background: $landing-primary-color;
    color: #fff;
    border-color: $landing-primary-color;
  }
}

.plan-panel {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  background: #fff;
  border: 1px solid $landing-border-light;
  border-radius: 24px;
  overflow: hidden;
}

.plan-main {
  background: $landing-primary-color;
  color: #f3f7f8;
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  h3 {
    font-size: 1.75rem;
    font-weight: 800;
    margin: 0;
  }

  .badge {
    align-self: flex-start;
    background: $landing-secondary-color;
    color: $landing-primary-color;
    font-weight: 700;
    font-size: 0.8rem;
    border-radius: 999px;
    padding: 4px 12px;
  }
}

.interval-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 12px 0 8px;

  button {
    border: 1px solid $landing-border-light;
    background: #fff;
    color: $landing-text-dark;
    border-radius: 999px;
    padding: 6px 14px;
    font-weight: 600;

    &[aria-pressed='true'] {
      background: $landing-primary-color;
      color: #fff;
      border-color: $landing-primary-color;
    }
  }
}

.interval-note {
  color: $landing-text-medium;
  font-size: 0.95rem;
  margin: 0 0 12px;
}

.plan-price {
  font-weight: 900;
  font-size: clamp(3rem, 8vw, 5.5rem);
  line-height: 0.9;

  small {
    font-size: 1.1rem;
    font-weight: 600;
    color: #b9cad6;
  }
}

.plan-was {
  color: #b9cad6;
  font-size: 0.95rem;
}

.plan-mkt {
  font-size: 0.9rem;
  color: $landing-secondary-color;
}

.plan-for {
  font-size: 1.05rem;
  line-height: 1.45;
  color: #e2edf2;
  border-top: 1px solid #2c4a66;
  padding-top: 16px;
  margin: 8px 0 0;
}

.fam-sizes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  button {
    border: 1px solid #2c4a66;
    background: transparent;
    color: #e2edf2;
    border-radius: 8px;
    padding: 6px 12px;
    font-weight: 600;
    cursor: pointer;

    &[aria-pressed='true'] {
      background: $landing-secondary-color;
      color: $landing-primary-color;
      border-color: $landing-secondary-color;
    }
  }
}

.btn-plan-cta {
  margin-top: auto;
  align-self: flex-start;
  background: $landing-secondary-color;
  color: $landing-primary-color;
  font-weight: 700;
  border: 0;
  border-radius: 10px;
  padding: 12px 20px;
  cursor: pointer;

  i {
    color: $landing-primary-color !important;
  }

  &:hover:not(:disabled) {
    background: $landing-blue-light;
    color: $landing-primary-color;

    i {
      color: $landing-primary-color !important;
    }
  }

  &--soon,
  &:disabled {
    cursor: not-allowed;
    background: rgba(255, 255, 255, 0.18);
    color: #e2edf2;

    &:hover {
      background: rgba(255, 255, 255, 0.18);
      color: #e2edf2;
    }
  }
}

.benefits {
  padding: 32px 36px;

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    padding: 14px 0;
    border-bottom: 1px solid $landing-border-light;

    &:last-child {
      border-bottom: 0;
    }

    b {
      display: block;
      color: $landing-primary-color;
      font-size: 1.05rem;
      margin-bottom: 4px;
    }

    span {
      color: $landing-text-medium;
      font-size: 0.95rem;
    }
  }
}

@media (max-width: 860px) {
  .plan-panel {
    grid-template-columns: 1fr;
  }

  .plans-tabs-section {
    padding: 48px 16px;
  }

  .plan-main {
    padding: 28px 22px;
  }

  .benefits {
    padding: 24px 20px;
  }

  .plans-bar {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
